import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import TextField from "@material-ui/core/TextField";
import RouterIcon from "@material-ui/icons/Router";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WifiIcon from "@material-ui/icons/Wifi";
import Card from "common/components/Card/Card.jsx";
import CardHeader from "common/components/Card/CardHeader.jsx";
import CardIcon from "common/components/Card/CardIcon.jsx";
import CardBody from "common/components/Card/CardBody.jsx";
import GridContainer from "common/components/Grid/GridContainer.jsx";
import GridItem from "common/components/Grid/GridItem.jsx";
import Button from "common/components/CustomButtons/Button";
import ErrorBox from "common/components/ErrorBox";
import LoadingBox from "common/components/LoadingBox";
import * as api from "core-module/services/api";
import config from "config.js";
import dashboardStyle from "assets/jss/material-dashboard-pro-react/views/dashboardStyle";
import withStyles from "@material-ui/core/styles/withStyles";
import PackageSpeedDisplay from "views/Internet/PackageSpeedDisplay.jsx";
import PromoPackageSelection from "./PromoPackageSelection.jsx";

const styles = {
  cardCategory: dashboardStyle.cardCategory,
  cardIconTitle: dashboardStyle.cardIconTitle,
  cardTitle: { ...dashboardStyle.cardTitle, margin: "20px 0" },
  sectionIntro: {
    color: "#5f6f7e",
    margin: "4px 0 18px",
    lineHeight: 1.5,
  },
  packageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "18px",
    marginTop: "20px",
    marginBottom: "34px",
  },
  packageCard: {
    border: "1px solid #d7e3ef",
    borderRadius: "8px",
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 12px 28px rgba(20, 62, 103, 0.08)",
    cursor: "pointer",
    transition:
      "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
  },
  packageCardSelected: {
    borderColor: "#1aa6d9",
    boxShadow: "0 16px 34px rgba(26, 166, 217, 0.26)",
  },
  packageHeader: {
    minHeight: "92px",
    padding: "18px 20px",
    background: "#1f77a6",
    color: "#fff",
    position: "relative",
  },
  packageHeaderFeatured: {
    background: "#c4314d",
  },
  packageName: {
    margin: 0,
    color: "#fff",
    fontSize: "20px",
    fontWeight: 700,
    lineHeight: 1.25,
  },
  packageSubhead: {
    margin: "10px 0 0",
    color: "#fff",
    fontSize: "13px",
  },
  selectedIcon: {
    position: "absolute",
    top: "16px",
    right: "16px",
    color: "#fff",
  },
  packageBody: {
    padding: "18px 20px 20px",
  },
  packageSelectLine: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "8px",
    color: "#123e68",
    fontWeight: 700,
  },
  packageCheckbox: {
    width: "32px",
    height: "32px",
    borderRadius: "6px",
    border: "2px solid #1aa6d9",
    background: "#fff",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 6px 14px rgba(26, 166, 217, 0.16)",
    flex: "0 0 32px",
  },
  packageCheckboxSelected: {
    background: "#1aa6d9",
  },
  packageCheckboxIcon: {
    fontSize: "24px",
  },
  packageRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "16px",
    padding: "10px 0",
    borderBottom: "1px solid #e7edf4",
  },
  packageLabel: {
    color: "#173e6c",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  packageValue: {
    color: "#123e68",
    fontSize: "18px",
    fontWeight: 700,
    textAlign: "right",
  },
  packageUnit: {
    color: "#587594",
    fontSize: "12px",
    fontWeight: 600,
    marginLeft: "4px",
  },
  routerAddon: {
    marginTop: "0",
    padding: "18px 20px",
    border: "1px solid #cfe4ef",
    borderRadius: "8px",
    background: "#f6fbfe",
    display: "flex",
    gap: "18px",
    alignItems: "flex-start",
    cursor: "pointer",
    position: "relative",
    zIndex: 1,
    boxShadow: "0 8px 20px rgba(20, 62, 103, 0.06)",
  },
  routerAddonSelected: {
    borderColor: "#1aa6d9",
    background: "#eef9fd",
  },
  routerIcon: {
    width: "48px",
    height: "48px",
    borderRadius: "8px",
    color: "#fff",
    background: "#1aa6d9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 48px",
  },
  routerControl: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    flex: "0 0 54px",
  },
  routerCheckbox: {
    width: "34px",
    height: "34px",
    borderRadius: "6px",
    border: "2px solid #1aa6d9",
    background: "#fff",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 6px 14px rgba(26, 166, 217, 0.18)",
  },
  routerCheckboxSelected: {
    background: "#1aa6d9",
  },
  routerCheckboxIcon: {
    fontSize: "25px",
  },
  routerTitle: {
    margin: "0 0 6px",
    color: "#123e68",
    fontWeight: 700,
    fontSize: "18px",
  },
  routerCopy: {
    margin: "0 0 10px",
    color: "#5f6f7e",
    lineHeight: 1.5,
  },
  routerHighlights: {
    margin: 0,
    paddingLeft: "18px",
    color: "#173e6c",
    lineHeight: 1.6,
  },
  promoBox: {
    marginTop: "22px",
    padding: "16px 18px",
    borderRadius: "8px",
    background: "#fbfcfe",
    border: "1px solid #e1e8ef",
  },
  promoTitle: {
    margin: "0 0 6px",
    color: "#123e68",
    fontSize: "18px",
    fontWeight: 700,
  },
  promoCopy: {
    margin: "0 0 14px",
    color: "#5f6f7e",
    lineHeight: 1.5,
  },
  inlineError: {
    marginBottom: "18px",
    padding: "12px 14px",
    borderRadius: "6px",
    border: "1px solid #f3c2c2",
    background: "#fff5f5",
    color: "#b33a3a",
    fontWeight: 600,
  },
  disclaimerList: {
    margin: "18px 0 0",
    paddingLeft: "18px",
    color: "#173e6c",
    lineHeight: 1.55,
  },
  disclaimerItem: {
    marginTop: "7px",
  },
  actions: {
    marginTop: "24px",
  },
};

const getPackageName = (packageOption) =>
  packageOption.name ||
  packageOption.description ||
  packageOption.packagename ||
  packageOption.packageName ||
  "Internet Package";

const getPackageDetail = (packageOption, label) => {
  const detail = (packageOption.details || []).find((item) =>
    String(item)
      .toLowerCase()
      .startsWith(label.toLowerCase() + ":")
  );
  if (!detail) {
    return null;
  }
  return detail.split(":").slice(1).join(":").trim();
};

const PreinstallPackage = (props) => {
  const { classes } = props;
  const userid = useSelector((state) => state.services.ids.user);
  const [loading, setLoading] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [packageData, setPackageData] = useState(null);
  const [packageid, setPackageid] = useState("");
  const [needsrouter, setNeedsrouter] = useState(false);
  const [promocode, setPromocode] = useState("");

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError("");
    setPackageData(null);
    api
      .get("user." + userid + "/preinstallpackage")
      .then((response) => {
        if (!isMounted) {
          return;
        }
        api.requireApiSuccess(response, "Unable to load package options.");
        const data = response?.object || response?.list?.[0] || response;
        setPackageData(data);
        setPackageid(data?.selection?.packageid || "");
        setNeedsrouter(Boolean(data?.selection?.needsrouter));
        setPromocode(
          data?.selection?.promocode === "_NULL" ||
            data?.selection?.promocode === null ||
            data?.selection?.promocode === undefined
            ? ""
            : data.selection.promocode
        );
        setLoading(false);
      })
      .catch((e) => {
        if (!isMounted) {
          return;
        }
        setError(e.message || "Unable to load package options.");
        setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [userid, loadAttempt]);

  const savePackage = async () => {
    if (!packageid) {
      setError("Please select an internet package.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      api.requireApiSuccess(
        await api.post("user." + userid + "/preinstallpackage", {
          packageid: Number(packageid),
          needsrouter,
          promocode: promocode.trim(),
        }),
        "Unable to save package selection."
      );
      props.history.push(
        config.MYACCOUNT.APP_PATH + "/user/preinstallchecklist"
      );
    } catch (e) {
      setError(e.message || "Unable to save package selection.");
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingBox />;
  }

  if (error && !packageData) {
    return (
      <div>
        <ErrorBox>{error}</ErrorBox>
        <Button
          color="primary"
          onClick={() => setLoadAttempt((attempt) => attempt + 1)}
        >
          Try again
        </Button>
      </div>
    );
  }

  if (packageData?.mode === "promo_tiers") {
    return (
      <PromoPackageSelection
        key={userid}
        initialData={packageData}
        userid={userid}
        history={props.history}
      />
    );
  }

  return (
    <GridContainer justify="center">
      <GridItem xs={12} md={10}>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <RouterIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Select Internet Package</h4>
          </CardHeader>
          <CardBody>
            {error ? <div className={classes.inlineError}>{error}</div> : null}
            <p className={classes.sectionIntro}>
              Choose the internet plan that best fits your home. You can update
              router and promo details before saving.
            </p>
            <div className={classes.packageGrid}>
              {(packageData?.packages || []).map((packageOption, index) => {
                const id =
                  packageOption.packageid ||
                  packageOption.id ||
                  packageOption.packageId;
                const price =
                  packageOption.total_monthly_fee ||
                  packageOption.monthly_fee ||
                  packageOption.price ||
                  packageOption.monthlyfee ||
                  packageOption.monthlyFee ||
                  packageOption.amount;
                const isSelected = String(packageid) === String(id);
                const traffic = getPackageDetail(packageOption, "Traffic Cap");
                return (
                  <div
                    key={id}
                    className={
                      classes.packageCard +
                      (isSelected ? " " + classes.packageCardSelected : "")
                    }
                    onClick={() => setPackageid(id)}
                  >
                    <div
                      className={
                        classes.packageHeader +
                        (index === 0 ? " " + classes.packageHeaderFeatured : "")
                      }
                    >
                      {isSelected ? (
                        <CheckCircleIcon className={classes.selectedIcon} />
                      ) : null}
                      <h3 className={classes.packageName}>
                        {getPackageName(packageOption)}
                      </h3>
                      <p className={classes.packageSubhead}>
                        Free installation with a 2-year term*
                      </p>
                    </div>
                    <div className={classes.packageBody}>
                      <div className={classes.packageSelectLine}>
                        <div
                          className={
                            classes.packageCheckbox +
                            (isSelected
                              ? " " + classes.packageCheckboxSelected
                              : "")
                          }
                        >
                          {isSelected ? (
                            <CheckCircleIcon
                              className={classes.packageCheckboxIcon}
                            />
                          ) : null}
                        </div>
                        <span>
                          {isSelected ? "Selected package" : "Select package"}
                        </span>
                      </div>
                      <div className={classes.packageRow}>
                        <span className={classes.packageLabel}>Fee</span>
                        <span className={classes.packageValue}>
                          ${price}
                          <span className={classes.packageUnit}>/month</span>
                        </span>
                      </div>
                      <div className={classes.packageRow}>
                        <span className={classes.packageLabel}>Traffic</span>
                        <span className={classes.packageValue}>
                          {traffic || "Included"}
                        </span>
                      </div>
                      <PackageSpeedDisplay
                        packageInfo={packageOption}
                        legacyRateUnit="kbps"
                        renderRow={(speed) => (
                          <div
                            key={speed.direction}
                            className={classes.packageRow}
                          >
                            <span className={classes.packageLabel}>
                              {speed.label}
                            </span>
                            <span className={classes.packageValue}>
                              {speed.value}
                            </span>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div
              className={
                classes.routerAddon +
                (needsrouter ? " " + classes.routerAddonSelected : "")
              }
              onClick={() => setNeedsrouter(!needsrouter)}
            >
              <div className={classes.routerControl}>
                <div className={classes.routerIcon}>
                  <WifiIcon />
                </div>
                <div
                  className={
                    classes.routerCheckbox +
                    (needsrouter ? " " + classes.routerCheckboxSelected : "")
                  }
                >
                  {needsrouter ? (
                    <CheckCircleIcon className={classes.routerCheckboxIcon} />
                  ) : null}
                </div>
              </div>
              <div>
                <h4 className={classes.routerTitle}>
                  Add managed WiFi Router for $
                  {packageData?.routermonthlyfee || "4.95"}/month
                </h4>
                <p className={classes.routerCopy}>
                  {packageData?.routerpromo || "First three months are free."}{" "}
                  An MCSnet router is simple to set up and easy to manage from
                  the app.
                </p>
                <ul className={classes.routerHighlights}>
                  <li>Set your WiFi name and password without hassle.</li>
                  <li>Create guest networks for visitors.</li>
                  <li>Keep support simple with MCSnet-managed hardware.</li>
                </ul>
              </div>
            </div>
            <div className={classes.promoBox}>
              <h4 className={classes.promoTitle}>Promo or Referral Code</h4>
              <p className={classes.promoCopy}>
                If you have a promo code or a referral code from a friend, enter
                it here and we will apply it to your package selection.
              </p>
              <TextField
                fullWidth
                label="Promo or referral code"
                margin="normal"
                variant="outlined"
                value={promocode}
                onChange={(event) => setPromocode(event.target.value)}
              />
            </div>
            <ul className={classes.disclaimerList}>
              <li className={classes.disclaimerItem}>
                All packages include 1 dynamic IP address.
              </li>
              <li className={classes.disclaimerItem}>
                Pre-authorized payment (PAP) through bank (EFT) or credit card
                is required for all plans.
              </li>
              <li className={classes.disclaimerItem}>
                We are an "Up to" service, and maximum internet speed is not
                guaranteed. Speeds may vary depending on the tower equipment
                servicing your site, your radio signal to that tower, the
                construction materials of your home, location and configuration
                of your WiFi router, location and capabilities of WiFi devices,
                number of active WiFi devices or other factors.
              </li>
            </ul>
            <div className={classes.actions}>
              <Button color="primary" disabled={saving} onClick={savePackage}>
                Save Package
              </Button>
              <Link
                to={config.MYACCOUNT.APP_PATH + "/user/preinstallchecklist"}
              >
                <Button color="default">Back</Button>
              </Link>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
};

export default withStyles(styles)(PreinstallPackage);
